/**
 * API Layer Main Export
 * Tüm API katmanını dışa aktaran merkezi dosya
 */

// ==================== CONFIGURATION ====================
export * from './config/api.config';
export * from './config/endpoints';
import { API_CONFIG } from './config/api.config';
import { ENDPOINTS } from './config/endpoints';

// ==================== TYPES ====================
export * from './types/api.types';
export * from './types/requests/auth.types';
export * from './types/requests/alarm.types';
export * from './types/requests/unit.types';
export * from './types/responses/auth.types';
export * from './types/responses/alarm.types';
export * from './types/responses/unit.types';

// ==================== CLIENT ====================
export * from './client/httpClient.interface';
export { httpClient, HttpClientFactory } from './client/axiosClient';
export { apiClient } from './client/apiClient';
import { httpClient, HttpClientFactory } from './client/axiosClient';
import { apiClient } from './client/apiClient';

// Interceptors
import requestInterceptorManager from './client/interceptors/request.interceptor';
import errorInterceptorManager from './client/interceptors/error.interceptor';
import authInterceptor from './client/interceptors/auth.interceptor';
import loggingInterceptorManager from './client/interceptors/logging.interceptor';

// ==================== SERVICES ====================
export * from './services/auth.service';
export * from './services/alarm.service';
export * from './services/unit.service';
export * from './services/wagon.service';
export * from './services/fleet.service';
export * from './services/user.service';
export * from './services/tenant.service';
export * from './services/building.service';
export * from './services/notification.service';

// Services index
import services from './services';
export { ServiceFactory, ServiceRegistry } from './services';

// ==================== HOOKS ====================
export * from './hooks/useApiQuery';
export * from './hooks/useAuth';
import hooks from './hooks';

// ==================== UTILITIES ====================
export * from './utils/api.utils';
export * from './utils/query.utils';

// ==================== CONSTANTS ====================
export * from './constants/api.constants';

// ==================== API INITIALIZATION ====================

/**
 * API katmanını initialize et
 * Uygulama başlangıcında çağırılmalı
 */
export function initializeAPI(options?: {
  baseURL?: string;
  enableLogging?: boolean;
  enableCache?: boolean;
  onError?: (error: any) => void;
}): void {
  console.log('🚀 Initializing API Layer...');
  
  // Config overrides
  if (options?.baseURL) {
    apiClient.setBaseURL(options.baseURL);
  }
  
  // Error handler
  if (options?.onError) {
    httpClient.addErrorHandler(options.onError);
  }
  
  // Services initialization
  services.initialize();
  
  // Hooks initialization
  hooks.initialize();
  
  // Health check
  setTimeout(async () => {
    try {
      const health = await services.checkHealth();
      console.log('✅ API Services Health:', health);
    } catch (error) {
      console.warn('⚠️ API health check failed:', error);
    }
  }, 1000);
  
  console.log('✅ API Layer initialized successfully');
}

/**
 * API katmanını temizle
 * Logout veya uygulama cleanup'ında çağırılmalı
 */
export function cleanupAPI(): void {
  console.log('🧹 Cleaning up API Layer...');
  
  // Services cleanup
  services.cleanup();
  
  // Clear all caches
  apiClient.cancelAllRequests();
  
  // Clear interceptors
  requestInterceptorManager.clear();
  errorInterceptorManager.clear();
  
  console.log('✅ API Layer cleaned up');
}

/**
 * API durumunu kontrol et
 */
export async function checkAPIHealth(): Promise<{
  status: 'healthy' | 'degraded' | 'unhealthy';
  services: Record<string, boolean>;
  timestamp: string;
}> {
  const servicesHealth = await services.checkHealth();
  
  const allHealthy = Object.values(servicesHealth).every(status => status === true);
  const anyHealthy = Object.values(servicesHealth).includes(true);
  let status: 'healthy' | 'degraded' | 'unhealthy';

  if (allHealthy) {
      status = 'healthy';
  } else if (anyHealthy) {
      status = 'degraded';
  } else {
      status = 'unhealthy';
  }
  return {
    status: status,
    services: servicesHealth,
    timestamp: new Date().toISOString()
  };
}

/**
 * API versiyon bilgisi
 */
export const API_VERSION = {
  client: '1.0.0',
  layer: '1.0.0',
  compatibility: '>=1.0.0'
} as const;

// ==================== ERROR HANDLING ====================

/**
 * Global API error handler
 */
export class APIErrorHandler {
  private static handlers: Array<(error: any) => void> = [];
  
  static register(handler: (error: any) => void): void {
    this.handlers.push(handler);
  }
  
  static unregister(handler: (error: any) => void): void {
    const index = this.handlers.indexOf(handler);
    if (index > -1) {
      this.handlers.splice(index, 1);
    }
  }
  
  static handle(error: any): void {
    console.error('🔴 API Error:', error);
    
    for (const handler of this.handlers) {
      try {
        handler(error);
      } catch (handlerError) {
        console.error('Error handler failed:', handlerError);
      }
    }
    
    // Dispatch global error event
    const event = new CustomEvent('api-error', {
      detail: { error, timestamp: new Date().toISOString() }
    });
    globalThis.dispatchEvent(event);
  }
  
  static clear(): void {
    this.handlers = [];
  }
}

// ==================== EVENT SYSTEM ====================

/**
 * API event system
 */
export class APIEventSystem {
  private static readonly listeners: Map<string, Array<(data: any) => void>> = new Map();
  
  static on(event: string, callback: (data: any) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    
    this.listeners.get(event)!.push(callback);
    
    // Cleanup function
    return () => {
      const listeners = this.listeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }
  
  static emit(event: string, data?: any): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      for (const callback of listeners) {
        try {
          callback(data);
        } catch (error) {
          console.error(`Event handler error for ${event}:`, error);
        }
      }
    }
  }
  
  static clear(): void {
    this.listeners.clear();
  }
}

// Pre-defined events
export const API_EVENTS = {
  // Auth events
  AUTH_LOGIN: 'api:auth:login',
  AUTH_LOGOUT: 'api:auth:logout',
  AUTH_SESSION_EXPIRED: 'api:auth:session-expired',
  
  // Data events
  DATA_UPDATED: 'api:data:updated',
  DATA_CREATED: 'api:data:created',
  DATA_DELETED: 'api:data:deleted',
  
  // Network events
  NETWORK_ONLINE: 'api:network:online',
  NETWORK_OFFLINE: 'api:network:offline',
  
  // Error events
  ERROR_OCCURRED: 'api:error:occurred',
  
  // Loading events
  REQUEST_STARTED: 'api:request:started',
  REQUEST_COMPLETED: 'api:request:completed'
} as const;

// ==================== CACHE MANAGEMENT ====================

/**
 * Cache manager
 */
export class APICacheManager {
  static clearAll(): void {
    // Clear API client cache (using regex to match all keys)
    apiClient.clearCache(/.*/);
    
    // Clear service caches
    for (const [,service] of services.registry.getAll()) {
        if (service.clearCache) {
        service.clearCache();
      }
    }
    
    // Clear React Query cache
    if (typeof globalThis !== 'undefined') {
      const queryClient = (globalThis as any).__REACT_QUERY_CLIENT__;
      if (queryClient) {
        queryClient.clear();
      }
    }
    
    console.log('🧹 API cache cleared');
  }
  
  static clearByPattern(pattern: RegExp): void {
    // Implement pattern-based cache clearing
    console.log(`Clearing cache with pattern: ${pattern}`);
  }
  
  static getStats(): {
    size: number;
    entries: number;
    hitRate: number;
  } {
    // Implement cache statistics
    return {
      size: 0,
      entries: 0,
      hitRate: 0
    };
  }
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Request builder utility
 */
export function createRequest<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  data?: any,
  options?: any
) {
  return {
    endpoint,
    method,
    data,
    options,
    execute: async (): Promise<T> => {
      let response: any;
      
      switch (method) {
        case 'GET':
          response = await apiClient.customGet(endpoint, options);
          break;
        case 'POST':
          response = await apiClient.customPost(endpoint, data, options);
          break;
        case 'PUT':
          response = await apiClient.customPut(endpoint, data, options);
          break;
        case 'DELETE':
          response = await apiClient.customDelete(endpoint, options);
          break;
        case 'PATCH':
          response = await apiClient.customPatch(endpoint, data, options);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
      
      return (response?.data ?? response) as T;
    }
  };
}

/**
 * Debounced API call
 */
export function debounceRequest<T>(
  requestFn: () => Promise<T>,
  delay: number = 300
): () => Promise<T> {
  let timeoutId: NodeJS.Timeout;
  let pendingPromise: Promise<T> | null = null;
  
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    if (pendingPromise) {
      return pendingPromise;
    }
    
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          pendingPromise = requestFn();
          const result = await pendingPromise;
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          pendingPromise = null;
        }
      }, delay);
    });
  };
}

/**
 * Retry with exponential backoff
 */
export async function retryRequest<T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: unknown = undefined;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.log(`Retry attempt ${attempt}/${maxRetries} in ${delay}ms`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  if (lastError === undefined) {
    throw new Error('Retry request failed without an error');
  }
  
  // Error nesnesine normalize et
  throw normalizeError(lastError);
}
function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  if (error && typeof error === 'object' && 'message' in error) {
    return new Error(String((error as any).message));
  }
  return new Error(String(error));
}

/**
 * Batch requests
 */
export async function batchRequests<T>(
  requests: Array<() => Promise<T>>,
  concurrency: number = 5
): Promise<T[]> {
  const results: T[] = [];
  let index = 0;
  
  async function processBatch(): Promise<void> {
    while (index < requests.length) {
      const currentIndex = index++;
      try {
        const result = await requests[currentIndex]();
        results[currentIndex] = result;
      } catch (error) {
        results[currentIndex] = error as T;
      }
    }
  }
  
  const workers = Array.from({ length: Math.min(concurrency, requests.length) }, () => 
    processBatch()
  );
  
  await Promise.all(workers);
  return results;
}

// ==================== TYPE UTILITIES ====================

/**
 * Type guard for API response
 */
export function isApiResponse<T>(obj: any): obj is import('./types/api.types').ApiResponse<T> {
  return obj && typeof obj === 'object' && 'data' in obj && 'success' in obj;
}

/**
 * Type guard for paginated response
 */
export function isPaginatedResponse<T>(obj: any): obj is import('./types/api.types').PaginatedResponse<T> {
  return isApiResponse<T[]>(obj) && 'meta' in obj;
}

/**
 * Type guard for API error
 */
export function isApiError(obj: any): obj is import('./types/api.types').ApiErrorResponse {
  return obj && typeof obj === 'object' && 'status' in obj && 'message' in obj;
}

/**
 * Extract data from API response
 */
export function extractData<T>(response: any): T {
  if (isApiResponse<T>(response)) {
    return response.data;
  }
  return response;
}

// ==================== MAIN EXPORT OBJECT ====================

/**
 * Tüm API katmanını içeren ana nesne
 */
const api = {
  // Configuration
  config: {
    api: API_CONFIG,
    endpoints: ENDPOINTS
  },
  
  // Clients
  client: {
    http: httpClient,
    api: apiClient,
    factory: HttpClientFactory
  },
  
  // Services
  services,
  
  // Hooks
  hooks,
  
  // Interceptors
  interceptors: {
    request: requestInterceptorManager,
    error: errorInterceptorManager,
    auth: authInterceptor,
    logging: loggingInterceptorManager
  },
  
  // Utilities
  utils: {
    createRequest,
    debounceRequest,
    retryRequest,
    batchRequests,
    isApiResponse,
    isPaginatedResponse,
    isApiError,
    extractData
  },
  
  // Managers
  cache: APICacheManager,
  errors: APIErrorHandler,
  
  // Functions
  initialize: initializeAPI,
  cleanup: cleanupAPI,
  checkHealth: checkAPIHealth,
  
  // Info
  version: API_VERSION,
  events: API_EVENTS
};

// Global type için
declare global {
  interface Window {
    /**
     Debug için - sadece development'ta
     */
    __API__?: typeof api;
  }
}

// Development için global erişim
if (import.meta.env.DEV) {
  (globalThis as any).__API__ = api;
  console.log('🔧 API layer registered globally as window.__API__');
  
  // Hot reload support
  if (import.meta.hot) {
    import.meta.hot.accept(() => {
      console.log('🔄 API layer hot reloaded');
    });
  }
}

// ==================== DEFAULT EXPORT ====================

export default api;

// ==================== POLYFILLS / FALLBACKS ====================

/**
 * Offline fallback system
 */
export class OfflineFallback {
  private static readonly storageKey = 'api_offline_requests';
  private static isOnline = navigator.onLine;
  
  static init(): void {
    globalThis.addEventListener('online', () => {
      this.isOnline = true;
      this.syncPendingRequests();
    });
    
    globalThis.addEventListener('offline', () => {
      this.isOnline = false;
    });
    
    // Periodic sync
    setInterval(() => {
      if (this.isOnline) {
        this.syncPendingRequests();
      }
    }, 60000); // Her 1 dakikada
  }
  
  static queueRequest(request: {
    id: string;
    endpoint: string;
    method: string;
    data: any;
    timestamp: number;
  }): void {
    const pending = this.getPendingRequests();
    pending.push(request);
    localStorage.setItem(this.storageKey, JSON.stringify(pending));
  }
  
  private static getPendingRequests(): any[] {
    const stored = localStorage.getItem(this.storageKey);
    return stored ? JSON.parse(stored) : [];
  }
  
  private static async syncPendingRequests(): Promise<void> {
    const pending = this.getPendingRequests();
    
    for (const request of pending) {
      try {
        let response;
        switch (request.method) {
          case 'GET':
            response = await api.client.api.customGet(request.endpoint);
            break;
          case 'POST':
            response = await api.client.api.customPost(request.endpoint, request.data);
            break;
          case 'PUT':
            response = await api.client.api.customPut(request.endpoint, request.data);
            break;
          case 'DELETE':
            response = await api.client.api.customDelete(request.endpoint);
            break;
          default:
            console.warn(`Unsupported method: ${request.method}`);
            continue;
        }
        
        // Remove successful request
        this.removeRequest(request.id);
      } catch (error) {
        console.error('Failed to sync pending request:', error);
      }
    }
  }
  
  private static removeRequest(id: string): void {
    const pending = this.getPendingRequests();
    const filtered = pending.filter(req => req.id !== id);
    localStorage.setItem(this.storageKey, JSON.stringify(filtered));
  }
}

// Initialize offline fallback
if (typeof globalThis !== 'undefined') {
  OfflineFallback.init();
}

// ==================== PERFORMANCE MONITORING ====================

/**
 * API performance monitoring
 */
export class APIPerformanceMonitor {
  private static metrics: Array<{
    endpoint: string;
    method: string;
    duration: number;
    timestamp: number;
    success: boolean;
  }> = [];
  
  static start(endpoint: string, method: string): () => void {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      this.metrics.push({
        endpoint,
        method,
        duration,
        timestamp: Date.now(),
        success: true
      });
      
      // Keep only last 1000 metrics
      if (this.metrics.length > 1000) {
        this.metrics.shift();
      }
      
      // Log slow requests
      if (duration > 1000) {
        console.warn(`Slow API request: ${method} ${endpoint} took ${duration.toFixed(2)}ms`);
      }
    };
  }
  
  static getMetrics(): typeof this.metrics {
    return [...this.metrics];
  }
  
  static getAverageDuration(): number {
    if (this.metrics.length === 0) return 0;
    
    const total = this.metrics.reduce((sum, metric) => sum + metric.duration, 0);
    return total / this.metrics.length;
  }
  
  static clear(): void {
    this.metrics = [];
  }
}